// src/oura/docs/useSkillRun.ts
//
// The run that wrote a doc, kept current while it is running (#3983).
//
// Reads the Skill library (`GET /oura/skills`) and finds the run whose
// `documentId` is this doc, then asks again every few seconds until it is no
// longer `running` (bounded: `shouldPollRun`). The library lists only the last 5
// runs per doc type, so an old doc may find none, and then shows none.
//
// A failed read keeps what is already known: one dropped poll does not turn a
// running run into nothing. The result is tied to the doc it was read for, so a
// doc opened next never shows the previous doc's run for a commit.

import { useEffect, useState } from "react";
import { getSkillLibrary, type SkillRun } from "@service/aeo/clientDocsWrite";
import { RUN_POLL_MS, afterLibraryRead, runForDoc, shouldPollRun, type RunKnowledge } from "./skillRun";

interface Held {
    docId: string;
    run: SkillRun | null;
}

export function useSkillRun(
    docId: string | null,
    docType: string | null | undefined,
    {
        enabled,
        initial = null,
        expected = false,
    }: {
        /** Staff on a doc they can write to. Anything else and the library is not read. */
        enabled: boolean;
        /** The run Create answered with, shown until the library lists it. */
        initial?: SkillRun | null;
        /** A skill has just made this doc: wait for its run to be listed. */
        expected?: boolean;
    },
): SkillRun | null {
    const [held, setHeld] = useState<Held | null>(null);

    useEffect(() => {
        if (!enabled || !docId || !docType) return;
        let live = true;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const since = Date.now();
        let known: RunKnowledge = { run: initial, observed: false };

        const load = async () => {
            const r = await getSkillLibrary();
            if (!live) return;
            // A failed read keeps what is known; a successful one that no longer lists a run
            // seen before clears it (it left the library's window).
            if (r.ok) known = afterLibraryRead(known, runForDoc(r.data, docType, docId));
            setHeld({ docId, run: known.run });
            if (shouldPollRun(known.run, expected, since, Date.now())) timer = setTimeout(() => void load(), RUN_POLL_MS);
        };
        void load();

        return () => {
            live = false;
            if (timer) clearTimeout(timer);
        };
        // `initial` is read once for a doc: a new object each render must not restart the poll.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, docId, docType, expected]);

    if (!enabled || !docId) return null;
    // Before the first read lands: what Create answered with, for that doc only.
    if (held?.docId !== docId) return initial ?? null;
    return held.run;
}
